import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useServiceStatusStore } from '@/store/serviceStatusStore';

// Constants
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 300;
const GROUND_Y = 240;
const CX = 60;
const GRAVITY = 0.6;
const JUMP_FORCE = -13;
const INITIAL_SPEED = 4.5;
const SPEED_INCREMENT = 0.0005;
const OBSTACLE_INTERVAL_MIN = 60;
const OBSTACLE_INTERVAL_MAX = 120;

type GameState = 'idle' | 'playing' | 'dead';

interface Entity {
  x: number;
  width: number;
  height: number;
  type: 'obstacle' | 'coin' | 'tortoise';
  y?: number;
  // Tortoise-specific state.
  squashed?: boolean;
  squashTimer?: number;
  walkOffset?: number; // independent leftward drift so tortoise feels slower than the world.
}

const TORTOISE_WIDTH = 38;
const TORTOISE_HEIGHT = 24;
const TORTOISE_WALK_SPEED = 1.2; // extra leftward motion on top of world scroll.
const TORTOISE_SQUASH_FRAMES = 30;
const STOMP_BOUNCE = -10;

const CrocodileGame: React.FC = () => {
  const isOffline = useServiceStatusStore((s) => s.isOffline);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameStateRef = useRef<GameState>('idle');
  const manYRef = useRef(GROUND_Y - 32);
  const velYRef = useRef(0);
  const entitiesRef = useRef<Entity[]>([]);
  const scoreRef = useRef(0);
  const coinsRef = useRef(0);
  const speedRef = useRef(INITIAL_SPEED);
  const frameRef = useRef(0);
  const groundOffsetRef = useRef(0);
  const nextObsRef = useRef(OBSTACLE_INTERVAL_MAX);
  const rafRef = useRef<number>(0);
  const highScoreRef = useRef(0);

  const runnerImgRef = useRef<HTMLImageElement | null>(null);
  const runnerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [runnerProcessed, setRunnerProcessed] = useState(false);

  const [gameState, setGameState] = useState<GameState>('idle');
  const [displayScore, setDisplayScore] = useState(0);
  const [displayCoins, setDisplayCoins] = useState(0);
  // Track best score for the on-screen "HI" indicator (also kept in a ref for the rAF loop).
  const [, setHighScore] = useState(0);

  // Preload runner image and pre-process the transparency mask.
  useEffect(() => {
    const img = new Image();
    img.src = '/runner.png';
    img.onload = () => {
      const rCanvas = document.createElement('canvas');
      rCanvas.width = 60;
      rCanvas.height = 60;
      const tCtx = rCanvas.getContext('2d');
      if (tCtx) {
        tCtx.drawImage(img, 0, 0, 60, 60);
        const imageData = tCtx.getImageData(0, 0, 60, 60);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] > 240 && data[i + 1] > 240 && data[i + 2] > 240) {
            data[i + 3] = 0;
          }
        }
        tCtx.putImageData(imageData, 0, 0);
        runnerCanvasRef.current = rCanvas;
        setRunnerProcessed(true);
      }
    };
    runnerImgRef.current = img;
  }, []);

  const resetGame = useCallback(() => {
    manYRef.current = GROUND_Y - 32;
    velYRef.current = 0;
    entitiesRef.current = [];
    scoreRef.current = 0;
    coinsRef.current = 0;
    speedRef.current = INITIAL_SPEED;
    frameRef.current = 0;
    groundOffsetRef.current = 0;
    nextObsRef.current = OBSTACLE_INTERVAL_MAX;
    setDisplayScore(0);
    setDisplayCoins(0);
  }, []);

  const audioCtxRef = useRef<AudioContext | null>(null);

  const playSound = useCallback((type: 'jump' | 'coin' | 'dead') => {
    try {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new Ctor();
      }
      const audioCtx = audioCtxRef.current;
      if (audioCtx.state === 'suspended') audioCtx.resume();

      const now = audioCtx.currentTime;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);

      if (type === 'jump') {
        osc.type = 'square';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(600, now + 0.15);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
        osc.start();
        osc.stop(now + 0.15);
      } else if (type === 'coin') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(987, now);
        osc.frequency.setValueAtTime(1318, now + 0.08);
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.setValueAtTime(0.2, now + 0.08);
        gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
        osc.start();
        osc.stop(now + 0.25);
      } else if (type === 'dead') {
        [400, 300, 200].forEach((freq, i) => {
          const noteOsc = audioCtx.createOscillator();
          const noteGain = audioCtx.createGain();
          noteOsc.type = 'sawtooth';
          noteOsc.connect(noteGain);
          noteGain.connect(audioCtx.destination);

          const startTime = now + i * 0.15;
          noteOsc.frequency.setValueAtTime(freq, startTime);
          noteGain.gain.setValueAtTime(0.2, startTime);
          noteGain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);
          noteOsc.start(startTime);
          noteOsc.stop(startTime + 0.2);
        });
      }
    } catch {
      /* ignore */
    }
  }, []);

  const jump = useCallback(() => {
    const state = gameStateRef.current;
    if (state === 'idle' || state === 'dead') {
      resetGame();
      gameStateRef.current = 'playing';
      setGameState('playing');
      velYRef.current = JUMP_FORCE;
      playSound('jump');
    } else if (state === 'playing' && manYRef.current >= GROUND_Y - 34) {
      velYRef.current = JUMP_FORCE;
      playSound('jump');
    }
  }, [resetGame, playSound]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        jump();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [jump]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;

    const drawGround = (offset: number) => {
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, GROUND_Y + 28);
      ctx.lineTo(CANVAS_WIDTH, GROUND_Y + 28);
      ctx.stroke();
      ctx.fillStyle = '#9ca3af';
      for (let i = 0; i < 30; i++) {
        const x =
          ((i * 22 - offset * 0.5) % (CANVAS_WIDTH + 22) + CANVAS_WIDTH + 22) %
            (CANVAS_WIDTH + 22) -
          22;
        ctx.beginPath();
        ctx.arc(x, GROUND_Y + 32, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const drawMan = (y: number, f: number, isDead: boolean) => {
      ctx.save();
      ctx.translate(CX, y);
      if (isDead) {
        ctx.translate(30, 45);
        ctx.rotate(Math.PI / 2);
        ctx.translate(-30, -45);
      } else {
        ctx.translate(0, Math.sin(f * 0.2) * 2);
      }
      if (runnerCanvasRef.current && runnerProcessed) {
        ctx.drawImage(runnerCanvasRef.current, 0, 0, 60, 60);
      } else {
        ctx.fillStyle = '#ff0000';
        ctx.fillRect(10, 10, 40, 40);
      }
      ctx.restore();
    };

    const drawPipe = (x: number, h: number) => {
      ctx.save();
      ctx.fillStyle = '#008000';
      ctx.fillRect(x, GROUND_Y + 28 - h, 36, h);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.strokeRect(x, GROUND_Y + 28 - h, 36, h);
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(x - 4, GROUND_Y + 28 - h, 44, 14);
      ctx.strokeRect(x - 4, GROUND_Y + 28 - h, 44, 14);
      ctx.restore();
    };

    const drawCoin = (x: number, y: number, f: number) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(Math.abs(Math.sin(f * 0.1)), 1);
      ctx.fillStyle = '#ffd700';
      ctx.beginPath();
      ctx.arc(0, 0, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#b8860b';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    };

    const drawTortoise = (
      x: number,
      f: number,
      squashed: boolean,
      squashTimer: number
    ) => {
      const baseY = GROUND_Y + 28;
      const flatten = squashed ? Math.min(1, squashTimer / 6) : 0;
      const h = TORTOISE_HEIGHT * (1 - flatten * 0.7);
      const w = TORTOISE_WIDTH;
      const top = baseY - h;
      const bob = squashed ? 0 : Math.sin(f * 0.25) * 1.2;

      ctx.save();
      ctx.translate(x, top + bob);

      if (!squashed) {
        // Legs.
        ctx.fillStyle = '#92400e';
        const legPhase = Math.floor(f / 6) % 2 === 0 ? 1 : -1;
        ctx.fillRect(4, h - 2, 6, 4 + legPhase);
        ctx.fillRect(w - 10, h - 2, 6, 4 - legPhase);
        // Head.
        ctx.fillStyle = '#16a34a';
        ctx.beginPath();
        ctx.ellipse(-2, h * 0.55, 8, 6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#0f5132';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Eye.
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(-3, h * 0.5, 1.6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#000';
        ctx.beginPath();
        ctx.arc(-3, h * 0.5, 0.8, 0, Math.PI * 2);
        ctx.fill();
      }

      // Shell — green dome.
      ctx.fillStyle = squashed ? '#15803d' : '#22c55e';
      ctx.beginPath();
      ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#0f5132';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Shell pattern hexagons.
      if (!squashed) {
        ctx.fillStyle = '#15803d';
        ctx.beginPath();
        ctx.arc(w / 2, h / 2 - 1, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(w / 2 - 8, h / 2 + 3, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(w / 2 + 8, h / 2 + 3, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    };

    const gameLoop = () => {
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      const bg = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
      bg.addColorStop(0, '#f0f9ff');
      bg.addColorStop(0.6, '#e0f2fe');
      bg.addColorStop(1, '#f0f9ff');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      drawGround(groundOffsetRef.current);

      const state = gameStateRef.current;
      if (state === 'playing') {
        frameRef.current++;
        groundOffsetRef.current += speedRef.current;
        speedRef.current += SPEED_INCREMENT;
        scoreRef.current++;
        if (scoreRef.current % 3 === 0) setDisplayScore(Math.floor(scoreRef.current / 3));

        velYRef.current += GRAVITY;
        manYRef.current += velYRef.current;

        let currentFloor = GROUND_Y - 32;
        entitiesRef.current.forEach((e) => {
          if (e.type === 'obstacle') {
            const pR = CX + 45;
            const pL = CX + 15;
            const eR = e.x + e.width;
            const eL = e.x;
            if (pR > eL && pL < eR) {
              const pipeTop = GROUND_Y + 28 - e.height - 60;
              if (
                manYRef.current >= pipeTop - 10 &&
                manYRef.current <= pipeTop + 10 &&
                velYRef.current >= 0
              ) {
                currentFloor = pipeTop;
              }
            }
          }
          // A squashed tortoise becomes a small platform you can stand on briefly.
          if (e.type === 'tortoise' && e.squashed) {
            const pR = CX + 45;
            const pL = CX + 15;
            if (pR > e.x && pL < e.x + e.width) {
              const shellTop = GROUND_Y + 28 - e.height * 0.35 - 60;
              if (
                manYRef.current >= shellTop - 10 &&
                manYRef.current <= shellTop + 10 &&
                velYRef.current >= 0
              ) {
                currentFloor = shellTop;
              }
            }
          }
        });

        if (manYRef.current >= currentFloor) {
          manYRef.current = currentFloor;
          velYRef.current = 0;
        }

        nextObsRef.current--;
        if (nextObsRef.current <= 0) {
          // Roll: 45% pipe, 25% tortoise, 30% coin.
          const roll = Math.random();
          if (roll < 0.45) {
            entitiesRef.current.push({
              x: CANVAS_WIDTH + 20,
              width: 36,
              height: 24 + Math.random() * 40,
              type: 'obstacle',
            });
          } else if (roll < 0.7) {
            entitiesRef.current.push({
              x: CANVAS_WIDTH + 20,
              width: TORTOISE_WIDTH,
              height: TORTOISE_HEIGHT,
              type: 'tortoise',
              walkOffset: 0,
            });
          } else {
            entitiesRef.current.push({
              x: CANVAS_WIDTH + 20,
              y: GROUND_Y - 40 - Math.random() * 80,
              width: 20,
              height: 20,
              type: 'coin',
            });
          }
          nextObsRef.current =
            OBSTACLE_INTERVAL_MIN +
            Math.floor(Math.random() * (OBSTACLE_INTERVAL_MAX - OBSTACLE_INTERVAL_MIN));
        }

        entitiesRef.current = entitiesRef.current.filter((e) => {
          e.x -= speedRef.current;

          // Live tortoise also walks left a bit relative to the ground.
          if (e.type === 'tortoise' && !e.squashed) {
            e.x -= TORTOISE_WALK_SPEED;
          }

          // Squashed tortoise: stays on the ground, ages out, can't hurt the player.
          if (e.type === 'tortoise' && e.squashed) {
            e.squashTimer = (e.squashTimer ?? 0) + 1;
            if ((e.squashTimer ?? 0) > TORTOISE_SQUASH_FRAMES) return false;
            return e.x > -60;
          }

          const pR = CX + 44;
          const pL = CX + 16;
          const pT = manYRef.current + 8;
          const pB = manYRef.current + 58;
          const eR = e.x + e.width;
          const eL = e.x;

          let eT: number;
          let eB: number;
          if (e.type === 'obstacle') {
            eT = GROUND_Y + 28 - e.height;
            eB = GROUND_Y + 28;
          } else if (e.type === 'tortoise') {
            eT = GROUND_Y + 28 - e.height;
            eB = GROUND_Y + 28;
          } else {
            eT = (e.y || 0) - 10;
            eB = (e.y || 0) + 10;
          }

          if (pR > eL && pL < eR && pB > eT && pT < eB) {
            if (e.type === 'coin') {
              coinsRef.current++;
              setDisplayCoins(coinsRef.current);
              playSound('coin');
              return false;
            }

            if (e.type === 'tortoise') {
              // Mario-style stomp: player must be coming down AND their feet are
              // near the top of the shell.
              const isFalling = velYRef.current > 0;
              const landedOnHead = pB <= eT + 14;
              if (isFalling && landedOnHead) {
                e.squashed = true;
                e.squashTimer = 0;
                velYRef.current = STOMP_BOUNCE;
                coinsRef.current += 5;
                setDisplayCoins(coinsRef.current);
                playSound('coin');
                return true;
              }
              // Side hit → die.
              gameStateRef.current = 'dead';
              setGameState('dead');
              playSound('dead');
              const s = Math.floor(scoreRef.current / 3);
              if (s > highScoreRef.current) {
                highScoreRef.current = s;
                setHighScore(s);
              }
              return true;
            }

            // obstacle (pipe) — die if hit from the side (i.e. below the top lip).
            if (pB > eT + 12) {
              gameStateRef.current = 'dead';
              setGameState('dead');
              playSound('dead');
              const s = Math.floor(scoreRef.current / 3);
              if (s > highScoreRef.current) {
                highScoreRef.current = s;
                setHighScore(s);
              }
            }
          }
          return e.x > -60;
        });
      }

      entitiesRef.current.forEach((e) => {
        if (e.type === 'obstacle') {
          drawPipe(e.x, e.height);
        } else if (e.type === 'tortoise') {
          drawTortoise(e.x, frameRef.current, !!e.squashed, e.squashTimer ?? 0);
        } else {
          drawCoin(e.x, e.y || 0, frameRef.current);
        }
      });
      drawMan(manYRef.current, frameRef.current, state === 'dead');

      ctx.fillStyle = '#9ca3af';
      ctx.font = 'bold 13px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(
        `HI ${highScoreRef.current.toString().padStart(5, '0')}`,
        CANVAS_WIDTH - 14,
        22
      );

      rafRef.current = requestAnimationFrame(gameLoop);
    };

    rafRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [runnerProcessed, playSound]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 80,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        userSelect: 'none',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 32px',
          borderBottom: '1px solid #e5e7eb',
          background: '#fff',
        }}
      >
        <img src="/logo.svg" alt="CentraJob" style={{ height: 34 }} />
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 20px',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          ↻ Retry
        </button>
      </div>

      {/* Info Section */}
      <div style={{ textAlign: 'center', maxWidth: 520, marginBottom: 30 }}>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: '#111827', marginBottom: 12 }}>
          {isOffline ? 'You are Offline' : 'Service Unavailable'}
        </h1>
        <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.7, marginBottom: 22 }}>
          {isOffline
            ? 'Please check your internet connection and try again.'
            : 'Our servers are temporarily unable to handle your request. Please try again shortly.'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
              color: '#6b7280',
            }}
          >
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />
            <span>
              Click <strong>Retry</strong> to refresh manually
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
              color: '#6b7280',
            }}
          >
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e' }} />
            <span>Check back in a few minutes if the issue persists</span>
          </div>
        </div>
      </div>

      <div
        style={{
          width: 60,
          height: 2,
          background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)',
          marginBottom: 28,
        }}
      />

      {/* Game Section */}
      <div style={{ textAlign: 'center' }}>
        <p
          style={{
            fontSize: 12,
            color: '#9ca3af',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            marginBottom: 10,
          }}
        >
          While you wait — play a game! Press{' '}
          <kbd
            style={{
              background: '#f3f4f6',
              border: '1px solid #e5e7eb',
              borderRadius: 4,
              padding: '1px 6px',
              color: '#6b7280',
            }}
          >
            Space
          </kbd>{' '}
          or tap
        </p>

        <div
          onClick={jump}
          style={{
            position: 'relative',
            borderRadius: 12,
            overflow: 'hidden',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.05)',
            border: '1px solid #e5e7eb',
          }}
        >
          <canvas
            ref={canvasRef}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            style={{ display: 'block', maxWidth: '88vw' }}
          />

          {(gameState === 'idle' || gameState === 'dead') && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(3px)',
              }}
            >
              <div style={{ fontSize: 36, marginBottom: 8 }}>
                {gameState === 'idle' ? '🏃' : '💀'}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: gameState === 'idle' ? '#2563eb' : '#dc2626',
                  marginBottom: 4,
                }}
              >
                {gameState === 'idle' ? 'Coin Runner' : 'Game Over'}
              </div>
              {gameState === 'dead' && (
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>
                  Score: {displayScore} | Coins: {displayCoins}
                </div>
              )}
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Press Space / Tap to {gameState === 'idle' ? 'start' : 'restart'}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: 12,
            display: 'inline-flex',
            gap: 16,
            padding: '4px 16px',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 16,
            color: '#2563eb',
            fontSize: 12,
            fontWeight: 600,
            opacity: gameState === 'playing' ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
        >
          <span>Score: {displayScore}</span>
          <span>Coins: {displayCoins}</span>
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 16,
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 11,
          width: '100%',
          padding: '0 16px',
        }}
      >
        {isOffline
          ? 'You are currently offline. Check your connection.'
          : 'The server will be back soon. We apologize for the inconvenience.'}
      </div>
    </div>
  );
};

export default CrocodileGame;
